import type { ReportTypeId } from '../config/reportTypes';
import type { LiteratureEntry } from '../types/siteEvidence';
import { getReportLiteratureEntries } from './literatureQuality';
import { literaturePublicationLabel } from './literaturePublication';

/** Fixed field caps keep twelve publications below this char budget. */
export const LITERATURE_REPORT_CONTEXT_MAX_CHARS = 10000;

function bounded(value: string, limit: number): string {
  const text = value.replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

/** The same bounded research projection is used in the SED and at dispatch. */
export function buildLiteratureReportContext(
  entries: ReadonlyArray<LiteratureEntry> | undefined,
  reportType?: ReportTypeId,
): string {
  const selected = getReportLiteratureEntries(entries ?? [], reportType);
  if (!selected.length) return '';
  const lines = [
    `## RELEVANT PUBLICATIONS (${selected.length} entries)`,
    'Cite inline as [Ref N] using the authoritative numbered source list; do not infer indices from reading order.',
    'Research context only: assess study conditions and publication status before applying a finding. These publications do not establish site-specific water availability, utility capacity, permitting approval, or equipment performance. Metadata screening does not verify peer review or validate the study.',
    'Titles alone cannot substantiate a finding. Abstract excerpts are limited context; do not infer omitted results, quantitative claims, or site applicability.',
  ];
  for (const entry of selected) {
    const authors = bounded(entry.authors.slice(0, 5).join(', ') || 'Unknown author', 52)
      + (entry.authors.length > 5 ? ' et al.' : '');
    const year = bounded(String(entry.year), 8);
    const title = bounded(entry.title, 200);
    const journal = entry.journal ? `*${bounded(entry.journal, 60)}*` : `(${entry.provider})`;
    const label = bounded(literaturePublicationLabel(entry), 80);
    lines.push(`- ${label}; **${authors} ${year}** — ${title}. ${journal}. [Ref N]`);
    const snippet = bounded(entry.abstractSnippet?.trim() || entry.abstract?.trim() || '', 280);
    lines.push(snippet ? `  > ${snippet}` : '  > No abstract excerpt available; title and metadata support discovery only.');
  }
  return lines.join('\n');
}

/** Restore only a missing or partially truncated block, without changing refs. */
export function appendMissingLiteratureContext(promptText: string, context: string): string {
  return !context || promptText.includes(context) ? promptText : `${promptText}\n\n${context}`;
}
